import {
  Button,
  Dialog,
  H2,
  Icon,
  DialogNavigation,
  DialogContent,
} from "@sellar-io/components";

import { useState } from "react";
import { twMerge } from "tailwind-merge";
import { useLegalEntityId } from "../../hooks/api/v2/legalEntity/useLegalEntityId";
import { useSellarUser } from "../../hooks";
import { AlertContainer } from "../alert/AlertContainer";
import { useCreateTwoCustomerUserVerificationLink } from "../../hooks/api/v2/two/useCreateTwoCustomerUserVerficationLink";
import { Alert } from "../alert/Alert";
import { useBusinessTwoAccountVerificationEmail } from "../../hooks/api/v2/businessUser/useBusinessTwoAccountVerificationEmail";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { apiV2AssertError } from "../../hooks/api/v2/apiV2Request";
import { useLegalEntity } from "../../hooks/api/v2/legalEntity/useLegalEntity";
import { SellarCreditTwoSignature } from "../sellarCreditTwo/SellarCreditTwoSignature";

export const TwoSellarCreditVerificationRequiredAlert = ({
  fontSize,
  buttonFontSize,
  message,
}: {
  fontSize: "text-xs" | "text-sm" | "text-base";
  buttonFontSize: "text-sm" | "text-base";
  message: string;
}) => {
  const legalEntityId = useLegalEntityId();
  const { data: legalEntity } = useLegalEntity(legalEntityId, {
    staleTime: 1000 * 60 * 5,
    enabled: !!legalEntityId,
  });

  const legalEntityAdminUserEmail =
    legalEntity?.legalEntityAdminUserList[0]?.email;

  const { user } = useSellarUser();

  const [activeDialog, setActiveDialog] = useState<
    "changeUser" | "verifyTwoAccount"
  >();

  const isUserBusinessLegalEntityAdmin = !!user?.legalEntityAdminList.find(
    (legalEntityAdmin) => legalEntityAdmin.legalEntityId === legalEntityId,
  );

  return (
    <>
      <AlertContainer
        type={"warning"}
        className={twMerge("w-full text-xs", fontSize)}
      >
        <div className="flex flex-col justify-between">
          <div className="flex shrink-0 items-center justify-between gap-1">
            <div className="font-semibold text-orange-800">Action required</div>
            <Icon size={0.8} icon={"CAUTION"} className="text-orange-800" />
          </div>
          <div className="flex flex-col gap-1">
            <span className={"text-left text-zinc-800"}>{message}</span>
            <Button
              color="slim"
              className={twMerge("font-normal text-zinc-800", buttonFontSize)}
              onClick={() => {
                if (isUserBusinessLegalEntityAdmin) {
                  return setActiveDialog("verifyTwoAccount");
                }

                setActiveDialog("changeUser");
              }}
            >
              Verify account
            </Button>
          </div>
        </div>
      </AlertContainer>

      <PromptLegalEntityAdminVerificationDialog
        active={activeDialog === "changeUser"}
        legalEntityAdminUserEmail={legalEntityAdminUserEmail}
        onDismiss={() => setActiveDialog(undefined)}
      />

      <TwoVerificationDialog
        active={activeDialog === "verifyTwoAccount"}
        onDismiss={() => setActiveDialog(undefined)}
      />
    </>
  );
};

export const PromptLegalEntityAdminVerificationDialog = ({
  active,
  legalEntityAdminUserEmail,
  onDismiss,
}: {
  active: boolean;
  legalEntityAdminUserEmail?: string;
  onDismiss: () => void;
}) => {
  const { business } = useSellarCurrentBusiness();

  const businessTwoAccountVerificationEmailMutation =
    useBusinessTwoAccountVerificationEmail(business?.id ?? 0);

  const onClick = () => {
    businessTwoAccountVerificationEmailMutation
      .mutateAsync()
      .then(() => {
        onDismiss();
      })
      .catch((error) => {
        console.error(apiV2AssertError(error)?.response?.data.message);
      });
  };

  return (
    <Dialog
      active={active}
      fullHeight={true}
      onDismiss={onDismiss}
      className="rounded-none md:max-h-[768px] md:max-w-[480px] md:rounded-lg"
    >
      <DialogNavigation leftIcon={"CHEVRON_DOWN"} onClickLeftIcon={onDismiss} />

      <DialogContent className="flex h-full flex-col text-center">
        <div className="flex flex-1 flex-col items-center gap-4">
          <img
            src="https://sellar.imgix.net/sellar-image-assets/sellar-credit/send-link.svg"
            className="w-[230px] shrink-0"
            width={230}
            height={185}
          />
          <H2>
            Send your company director an account verification link to continue
            using Sellar Credit.
          </H2>

          {!!legalEntityAdminUserEmail && (
            <>
              <p className="text-zinc-800">
                <span>Your company director's email address</span>
              </p>

              <p className="text-zinc-800">
                <span className="font-semibold break-all">
                  {legalEntityAdminUserEmail}
                </span>
              </p>
            </>
          )}

          {businessTwoAccountVerificationEmailMutation.error && (
            <Alert
              type="error"
              title="Error sending magic link"
              message={
                apiV2AssertError(
                  businessTwoAccountVerificationEmailMutation.error,
                )?.response?.data.message
              }
              className="w-full"
            />
          )}
          <div className="flex w-full flex-col gap-2">
            <Button
              onClick={onClick}
              className="w-full"
              loading={businessTwoAccountVerificationEmailMutation.isPending}
            >
              Send magic link
            </Button>
          </div>
        </div>
        <SellarCreditTwoSignature />
      </DialogContent>
    </Dialog>
  );
};

export const TwoVerificationDialog = ({
  active,
  onDismiss,
}: {
  active: boolean;
  onDismiss: () => void;
}) => {
  const createTwoCustomerUserVerificationLink =
    useCreateTwoCustomerUserVerificationLink();

  return (
    <Dialog
      active={active}
      fullHeight={true}
      onDismiss={onDismiss}
      className="rounded-none md:max-h-[768px] md:max-w-[480px] md:rounded-lg"
    >
      <DialogNavigation leftIcon={"CHEVRON_DOWN"} onClickLeftIcon={onDismiss} />

      <DialogContent className="flex h-full flex-col items-center text-center">
        <div className="flex w-full flex-1 flex-col items-center gap-4">
          <img
            src="https://sellar.imgix.net/sellar-image-assets/app-assets/sellarxtwo.svg"
            className="my-2 w-[320px] shrink-0"
          />
          <H2>You will now be taken to Two’s portal to verify your account</H2>

          <p className="">
            To verify your account, you will be asked to either
          </p>

          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4 rounded-lg border border-zinc-200 p-4 text-left">
              <span className="flex-none rounded-lg bg-zinc-200 p-3">
                <Icon icon="EMAIL_LOCK_OUTLINE" size={1} />
              </span>
              <div className="flex flex-col gap-1">
                <span className="font-semibold">
                  Complete an email verification
                </span>
                <span className="text-sm">
                  You will only need your <b>Sellar login email</b>
                </span>
              </div>
            </div>

            <div>Or</div>

            <div className="flex items-center gap-4 rounded-lg border border-zinc-200 p-4 text-left">
              <span className="flex-none rounded-lg bg-zinc-200 p-3">
                <Icon icon="BANK_TRANSFER" size={1.1} />
              </span>
              <div className="flex flex-col gap-1">
                <span className="font-semibold">Verify using open banking</span>
                <span className="text-sm">
                  You will need your company’s bank details, and your{" "}
                  <b>Sellar login email</b>
                </span>
              </div>
            </div>
          </div>

          <div className="mt-6 flex w-full flex-col gap-2">
            <Button
              className="w-full"
              disabled={createTwoCustomerUserVerificationLink.isPending}
              onClick={() => {
                const returnUrl = `${import.meta.env.VITE_URL}/sellar-credit/two-direct-debit/set-up`;

                // create the verification link and redirect user
                createTwoCustomerUserVerificationLink
                  .mutateAsync({ returnUrl })
                  .then((result) => {
                    window.open(result.redirectUrl, "_blank");
                    onDismiss();
                  });
              }}
            >
              Continue
            </Button>
          </div>

          {createTwoCustomerUserVerificationLink.error && (
            <Alert
              type="error"
              title="Error redirecting to customer user verification"
              message={createTwoCustomerUserVerificationLink.error.message}
              className="w-full"
            />
          )}
        </div>
        <SellarCreditTwoSignature />
      </DialogContent>
    </Dialog>
  );
};
